import Immutable from 'immutable';
import { canvasSize, adStateLabelHeight, rulerThickness } from 'Constants/viewportLayout';
import { createSelector } from 'reselect';
import AdStateRecord from 'Records/AdStateRecord';
import { easelPositionFromScreenCoordinates, easelSizeInEaselCoordinates, easelSizeWithoutRulers } from 'Utils';

import {
  ADSTATES,
  COMPONENTS,
  KEYFRAMES,
  AD,
  APP,
  PRESENT,
  METADATA,
  FONTS,
  UI,
  VARIANTS,
} from 'Reducers/ad/utils';

export const getAd = state => state.getIn([APP, PRESENT, AD]);
export const getAdStates = state => state.getIn([APP, PRESENT, AD, ADSTATES]);
export const getFilteredAdSates = state => state.getIn([APP, PRESENT, AD, ADSTATES]).filter(adState => adState.name.indexOf('$$IVO-BUILD-GUIDE') === -1);
export const getComponents = state => state.getIn([APP, PRESENT, AD, COMPONENTS]);
export const getKeyframes = state => state.getIn([APP, PRESENT, AD, KEYFRAMES]);
export const getVariants = state => state.getIn([APP, PRESENT, AD, VARIANTS]);
export const getMetadata = state => state.getIn([APP, PRESENT, METADATA]);
export const getAdId = state => state.getIn([APP, PRESENT, METADATA, 'id']);
export const getActiveFonts = state => state.getIn([FONTS, 'activeFonts']);
export const getAvailableFonts = state => state.getIn([FONTS, 'fonts']);
export const getMetaKey = () => {
  if (navigator.platform.toLowerCase().includes('mac')) { // eslint-disable-line no-undef
    return '⌘';
  }
  return 'Ctrl';
};
export const getAssets = state => state.get('assets');

const EASEL_PADDING = 100;

export const makeGetComponentByKey = () =>
  createSelector(
    state => getComponents(state),
    (state, props) => props.componentKey,
    (components, key) => components.get(key),
  );

export const makeGetAdAstateByKey = createSelector(
  state => getAdStates(state),
  (state, props) => props.adStateKey,
  (adStates, key) => adStates.get(key),
);

export const getAssetIds = createSelector(
    state => getComponents(state),
    components => components.reduce((assetArray, component) => {
      if (typeof (component.getIn(['settings', 'assetIds'])) === 'undefined') {
        return assetArray;
      }
      return assetArray.concat(component.getIn(['settings', 'assetIds']).toArray());
    }, []),
  );

export const makeGetComponentImageAsset = () =>
  createSelector(
    state => getAssets(state),
    (state, props) => props.component,
    (assets, component) => {
      if (!component) return null;
      const componentAssetIds = component.getIn(['settings', 'assetIds']) || [];
      return assets.filter(asset =>
                      componentAssetIds.includes(asset.id)
                      &&
                      asset.mime_type.includes('image'),
                    ).sort((a, b) =>
                      componentAssetIds.indexOf(a.id)
                      >
                      componentAssetIds.indexOf(b.id),
                    )[0];
    },
  );

export const makeGetComponentImageAssets = () =>
  createSelector(
    state => getAssets(state),
    (state, props) => props.component,
    (assets, component) => {
      const componentAssetIds = component.getIn(['settings', 'assetIds']) || [];
      return assets.filter(asset =>
                      componentAssetIds.includes(asset.id)
                      &&
                      asset.mime_type.includes('image'),
                    ).sort((imageAssetInComponent1, imageAssetInComponent2) =>
                      componentAssetIds.indexOf(imageAssetInComponent1.id)
                      >
                      componentAssetIds.indexOf(imageAssetInComponent2.id),
                    );
    },
  );

export const makeGetComponentVideoAssets = () =>
  createSelector(
    state => getAssets(state),
    (state, props) => props.component,
    (assets, component) => {
      const componentAssetIds = component.getIn(['settings', 'assetIds']) || [];
      return assets.filter(asset =>
                      componentAssetIds.includes(asset.id)
                      &&
                      (asset.mime_type.includes('video') || asset.mime_type.includes('mpegurl')),
                    ).sort((imageAssetInComponent1, imageAssetInComponent2) =>
                      componentAssetIds.indexOf(imageAssetInComponent1.id)
                      >
                      componentAssetIds.indexOf(imageAssetInComponent2.id),
                    );
    },
  );

  export const makeGetComponentBackgroundSize = () =>
    createSelector(
      (state, props) => props.component,
      (component) => {
        if (!component) return null;
          const componentFitting = component.getIn(['settings', 'fitting']);
          if (componentFitting) {
            const fittingMap = { 'Original size': 'auto auto', Fit: 'cover', 'Fit (up to 100%)': 'contain', Stretch: '100% 100%' };
            const fittingStyle = fittingMap[componentFitting] || 'auto auto';
            return fittingStyle;
          }
         return '100% 100%';
        },
    );

  export const makeGetComponentBackgroundRepeat = () =>
    createSelector(
      (state, props) => props.component,
      (component) => {
        if (!component) return null;
          const componentFitting = component.getIn(['settings', 'fitting']);
          if (componentFitting) {
            const repeatBackground = componentFitting === 'Repeat';
            return repeatBackground ? 'repeat' : 'no-repeat';
          }
         return 'no-repeat';
        },
    );

  export const makeGetComponentBackgroundPositon = () =>
    createSelector(
      (state, props) => props.component,
      (component) => {
        if (!component) return null;
          const componentPosition = component.getIn(['settings', 'position']);
          if (componentPosition) {
            const positionMap = { 'Top-Left': '0% 0%', 'Top-Center': '50% 0%', 'Top-Right': '100% 0%', 'Center-Left': '0% 50%', Center: '50% 50%', 'Center-Right': '100% 50%', 'Bottom-Left': '0% 100%', 'Bottom-Center': '50% 100%', 'Bottom-Right': '100% 100%' };
            return positionMap[componentPosition] || 'center';
          }
         return 'center';
        },
    );

    export const makeGetVideoComponentBackgroundSize = () =>
      createSelector(
        (state, props) => props.component,
        (component) => {
          if (!component) return null;
            const componentFitting = component.getIn(['settings', 'fitting']);
            if (componentFitting) {
              const fittingMap = { 'Original size': { width: 'auto', height: 'auto' }, 'Fit to width': { width: '100%', height: 'auto' }, 'Fit to height': { width: 'auto', height: '100%' }, Crop: { width: 'auto', height: 'auto' }, Stretch: { width: '100%', height: '100%' } };
              const fittingStyle = fittingMap[componentFitting] || { width: '100%', height: '100%' };
              return fittingStyle;
            }
           return { width: '100%', height: '100%' };
          },
      );

    export const makeGetVideoComponentBackgroundPositon = () =>
      createSelector(
        (state, props) => props.component,
        (component) => {
          if (!component) return null;
            const defaultPosition = { top: '50%', left: '50%', transform: 'translateX(-50%) translateY(-50%)' };
            const componentPosition = component.getIn(['settings', 'position']);
            if (componentPosition) {
              const positionMap = { 'Top-Left': { top: 0, left: 0 }, 'Top-Center': { top: 0, left: '50%', transform: 'translateX(-50%)' }, 'Top-Right': { top: 0, left: 'auto', right: 0 }, 'Center-Left': { top: '50%', left: 0, transform: 'translateY(-50%)' }, 'Center-Right': { top: '50%', left: 'auto', right: 0, transform: 'translateY(-50%)' }, 'Bottom-Left': { top: 'auto', bottom: 0, left: 0 }, 'Bottom-Center': { top: 'auto', bottom: 0, left: '50%', transform: 'translateX(-50%)' }, 'Bottom-Right': { left: 'auto', right: 0, top: 'auto', bottom: 0 } };
              return positionMap[componentPosition] || defaultPosition;
            }
           return defaultPosition;
          },
      );

export const makeGetAdStateComponents = () =>
  createSelector(
    state => getAd(state),
    (state, props) => props.adState,
    (ad, adState) => adState.getComponents(ad),
  );

export const makeGetComponentsKeyframes = () =>
  createSelector(
    state => getKeyframes(state),
    (state, props) => props.component,
    (keyframes, component) =>
      component.keyframes.map(keyframeID => keyframes.get(keyframeID)),
  );

export const makeGetSelectedKeyframes = () => createSelector(
        state => getKeyframes(state),
        keyframes => keyframes.filter(keyframe => keyframe.selected),
    );

export const makeGetUnselectedKeyframes = () => createSelector(
    state => getKeyframes(state),
    keyframes => keyframes.filter(keyframe => !keyframe.selected),
  );

export const getAdStatesWithName = createSelector(
    state => getAdStates(state),
    (state, props) => props.name,
    (adStates, name) => adStates.filter(adState => adState.name === name),
);

export const getSelectedAdstate = createSelector(
  state => getAdStates(state),
  adStates => adStates.find(adState => adState.selected) || new AdStateRecord(),
);

export const getActiveGroup = createSelector(
  state => getComponents(state),
  components => components.find(c => c.isGroup() && c.isActive()),
);

export const getActiveGroupParent = (state) => {
  const components = getComponents(state);
  const activeGroup = getActiveGroup(state);
  return activeGroup ? components.find(c => c.subComponents.includes(activeGroup.key)) : null;
};

export const getActiveGroupParentName = (state) => {
  const adState = getSelectedAdstate(state);
  const activeGroupParent = getActiveGroupParent(state);
  return activeGroupParent ? activeGroupParent.getIn(['settings', 'name']) : adState.get('name');
};

export const getComponentParent = (state, group) => {
  const components = getComponents(state);
  return group ? components.find(c => c.subComponents.includes(group.key)) : null;
};


export const getActiveGroupParents = (state) => {
  let activeGroup = getActiveGroup(state);
  const navigationItems = [];
  while (activeGroup !== null) {
    if (activeGroup) navigationItems.push(activeGroup);
    activeGroup = getComponentParent(state, activeGroup);
  }
  return navigationItems;
};

export const getGroupsNavigation = (state) => {
  let activeGroup = getActiveGroup(state);
  const navigationItems = [];
  while (activeGroup !== null) {
    if (activeGroup) {
      navigationItems.push(activeGroup);
    } else {
      navigationItems.push(null);
    }
    activeGroup = getComponentParent(state, activeGroup);
  }
  return navigationItems;
};

export const getActiveGroupComponents = createSelector(
  state => getComponents(state),
  state => getActiveGroup(state),
  (components, activeGroup) => (activeGroup
    ? activeGroup.subComponents.map(c => components.get(c))
    : null),
);

export const getCurrentAdStateComponents = createSelector(
  state => getSelectedAdstate(state).components,
  state => getComponents(state),
  (componentKeys, components) => componentKeys.map(key => components.get(key)),
);

export const getActiveStageComponents = createSelector(
  state => getActiveGroupComponents(state),
  state => getCurrentAdStateComponents(state),
  (activeGroupComponents, adStateComponents) => (
    activeGroupComponents
    ||
    adStateComponents),
);

export const getCurrentAdStateSelectedComponents = createSelector(
  state => getCurrentAdStateComponents(state),
  state => getActiveGroupComponents(state),
  (components, activeGroupComponents) => (activeGroupComponents
    ? activeGroupComponents.filter(component => component.selected)
    : components.filter(component => component.selected)),
);

export const getCurrentAdStateHoveredComponent = createSelector(
  state => getCurrentAdStateComponents(state),
  state => getActiveGroupComponents(state),
  (components, activeGroupComponents) => (activeGroupComponents
      ? activeGroupComponents.find(component => component.hovered)
      : components.find(component => component.hovered)),

);

export const makeGetSelectedComponentsKeyframes = () =>
  createSelector(
    state => getKeyframes(state),
    state => getCurrentAdStateSelectedComponents(state),
    (keyframes, selectedComponents) =>
      selectedComponents.map(component =>
        component.keyframes.map(keyframeID => keyframes.get(keyframeID))),
    );

export const makeGetSelectedComponentsSelectedKeyframes = () =>
  createSelector(
    state => getKeyframes(state),
    state => getCurrentAdStateSelectedComponents(state),
    (keyframes, selectedComponents) =>
      selectedComponents.map(component =>
        component.keyframes.map(keyframeID => keyframes.get(keyframeID)).filter(keyframe => keyframe.selected),
      ));

export const getUpdatableKeyframes = (state) => {
  const selectedKeyframes = makeGetSelectedKeyframes()(state);
  const selectedComponents = getCurrentAdStateSelectedComponents(state);
  const firstComponent = selectedComponents.first();
  if (
      selectedComponents.count() === 1
    &&
      firstComponent.keyframes.count() === 1
    &&
      selectedKeyframes.count() === 0
  ) {
    return firstComponent.getKeyframes(state);
  }
  return selectedKeyframes;
};

export const makeSortKeyframes = () =>
  createSelector(
    keyframes => keyframes,
    keyframes => keyframes.sort((a, b) => a.t - b.t),
  );

export const aggregateStyle = styles => styles.reduce((reducer, style) => {
  Object.keys(style).forEach((key) => {
    if (reducer[key] !== style[key]) {
      reducer[key] = 'multiple';
    }
  });
  return reducer;
});

export const getAssetItemsFiltered = createSelector(
    state => state.get('folderAssets'),
    state => state.get('folders'),
    state => state.getIn([UI, 'assetFilterString']),
    state => state.getIn([UI, 'assetModalMimeTypes']),
    (assets, folders, filterString, mimeTypes) => {
      const mineTypesToNeverShow = ['video/mp4', 'video/webm', 'video/ogg', 'video/3gpp', 'video/x-theora+ogg', 'video/x-flv', 'application/vnd.apple.mpegurl', 'application/octet-stream'];
      const b = folders.filter(
        folder => folder.name.toLowerCase().includes(filterString)).toList();
      let a = assets.filter(
        asset => asset.name.toLowerCase().includes(filterString)
        &&
        !mineTypesToNeverShow.includes(asset.mime_type)).toList();
      if (mimeTypes.length > 0) a = a.filter(asset => mimeTypes.includes(asset.mime_type));
      return b.concat(a);
    },
);

export const getFontWeightsFiltered = createSelector(
  state => state.getIn([FONTS, 'fontWeights']),
  state => state.getIn([FONTS, 'selectedfont']),
  (weights, selected) => weights.get(selected) || Immutable.List(),
);

export const getFonts = createSelector(
  state => state.getIn([FONTS, 'fonts']),
  fonts => fonts,
);

export const getFontWeights = createSelector(
  state => state.getIn([FONTS, 'fontWeights']),
  fontWeights => fontWeights,
);

export const getFoldersFiltered = createSelector(
  state => state.get('folders'),
  state => state.getIn([UI, 'assetFilterString']),
  (folders, filterString) => {
    folders.filter(folder => folder.name.toLowerCase().indexOf(filterString) > -1);
  },
);

export const makeGetActiveGroupKeyframes = () =>
  createSelector(
    state => getKeyframes(state),
    state => getActiveGroupParents(state),
    (keyframes, groups) => groups
      ? groups.map(group => group.keyframes.map(keyframeID => keyframes.get(keyframeID)))
      : null,
  );


export const getSettingsDrawerOpen = createSelector(
  state => state.getIn([UI, 'settingsDrawerOpen']),
  settingsDrawerOpen => settingsDrawerOpen,
);

export const getAssetModalOpen = createSelector(
  state => state.getIn([UI, 'assetModalOpen']),
  assetModalOpen => assetModalOpen,
);

export const getSelectBoxOpen = createSelector(
  state => state.getIn([UI, 'selectBoxOpen']),
  selectBoxOpen => selectBoxOpen,
);

export const getCurrentAdStateLastKeyframe = createSelector(
  state => getCurrentAdStateComponents(state),
  state => getKeyframes(state),
  (components, keyframes) => keyframes.filter(k =>
    components.find(component => component.keyframes.includes(k.key)),
  ).sort((a, b) => a.t - b.t).last(),
);

export const getUILayerOffset = createSelector(
  state => state.getIn([UI, 'easelOffset']),
  state => state.getIn([UI, 'easelScale']),
  (easelOffset, easelScale) => [(easelOffset[0] - (canvasSize / 2)) * easelScale,
    (easelOffset[1] - (canvasSize / 2)) * easelScale],
);

const padRect = (rect, padding) => {
  let { width, height, top, right, bottom, left } = rect;

  if (width >= height) {
    top -= (padding * 0.5);
    left -= padding;
    width += (padding * 2);
    height += (padding * 2);
  } else {
    top -= (padding * 0.5);
    height += (padding * 2);
  }

  return {
    width,
    height,
    top,
    left,
    bottom,
    right,
  };
};

export const getBoundingRect = (adStates) => {
  if (!adStates.first()) {
    const canvasCenter = canvasSize / 2;
    return {
      top: canvasCenter,
      left: canvasCenter,
      width: 1000,
      height: 1000,
      bottom: canvasCenter + 1000,
      right: canvasCenter + 1000,
    };
  }
  const rect = {
    left: adStates.first().dimensions.get('left'),
    top: adStates.first().dimensions.get('top'),
    right: adStates.first().dimensions.get('left') + adStates.first().dimensions.get('width'),
    bottom: adStates.first().dimensions.get('top') + adStates.first().dimensions.get('height'),
  };
  adStates.forEach((state, index) => {
    if (index > 0) {
      if (state.dimensions.get('left') < rect.left) rect.left = state.dimensions.get('left');
      if (state.dimensions.get('top') < rect.top) rect.top = state.dimensions.get('top');
      const right = state.dimensions.get('left') + state.dimensions.get('width');
      if (right > rect.right) rect.right = right;
      const bottom = state.dimensions.get('top') + state.dimensions.get('height');
      if (bottom > rect.bottom) rect.bottom = bottom;
    }
  });
  rect.width = rect.right - rect.left;
  rect.height = rect.bottom - rect.top;
  return rect;
};

export const getAdStatesBoundingRect = createSelector(
  state => getAdStates(state),
  state => state.getIn([UI, 'easelScale']),
  (adStates, easelScale) => {
    const padding = EASEL_PADDING / easelScale;
    const rect = getBoundingRect(adStates);
    rect.top -= adStateLabelHeight / easelScale;
    rect.bottom += adStateLabelHeight / easelScale;
    return padRect(rect, padding);
  },
);

export const getActiveAdStateBoundingRect = createSelector(
  state => getSelectedAdstate(state),
  state => state.getIn([UI, 'easelScale']),
  (selectedAdState, easelScale) => {
    const padding = EASEL_PADDING / easelScale;
    const viewportRect = {
      top: selectedAdState.dimensions.get('top') - (adStateLabelHeight / easelScale),
      left: selectedAdState.dimensions.get('left'),
      width: selectedAdState.dimensions.get('width'),
      height: selectedAdState.dimensions.get('height'),
      right: selectedAdState.dimensions.get('left') + selectedAdState.dimensions.get('width'),
      bottom: selectedAdState.dimensions.get('top') + selectedAdState.dimensions.get('height') + (adStateLabelHeight / easelScale),
    };
    return padRect(viewportRect, padding);
  },
);

export const getEaselPositionFromScreen = createSelector(
    state => state.getIn([UI, 'easelOffset']),
    state => state.getIn([UI, 'easelScale']),
    (state, props) => props.cursorPos,
    (easelOffset, easelScale, cursorPos) =>
      easelPositionFromScreenCoordinates(cursorPos, easelOffset, easelScale),
);

export const getEaselMidPoint = createSelector(
  state => state.getIn([UI, 'easelOffset']),
  state => state.getIn([UI, 'easelScale']),
  state => state.getIn([UI, 'easelSize']),
  (easelOffset, easelScale, easelSize) => {
    const easelSizeConverted = easelSizeInEaselCoordinates(easelSize, easelScale);
    const xGap = easelSizeConverted.width / 2;
    const yGap = easelSizeConverted.height / 2;
    const x = ((canvasSize / 2) - easelOffset[0]) + xGap;
    const y = ((canvasSize / 2) - easelOffset[1]) + yGap;
    return { x, y };
  });

export const getEaselSizeWithoutRulers = createSelector(
  state => state.getIn([UI, 'easelSize']),
  easelSize => easelSizeWithoutRulers(easelSize),
);

export const getEaselScale = state => state.getIn([UI, 'easelScale']);

export const makeGetBaseDimension = (dimension) => {
  const getComponentsKeyframes = makeGetComponentsKeyframes();
  const sortKeyframes = makeSortKeyframes();

  return createSelector(
    (state, props) => getComponentsKeyframes(state, props),
    keyframes => sortKeyframes(keyframes),
    (sortedKeyframes) => {
      if (sortedKeyframes.count() === 0) {
        return {};
      }

      const keyframe = sortedKeyframes.find(kf =>
        kf.style.get(dimension || 0) > 0,
      );
      const borderWidth = sortedKeyframes.find(kf =>
        kf.style.get('borderWidth' || 0) > 0,
      );
      return keyframe
        ? parseInt(keyframe.style.get(dimension), 10) + ((parseInt(keyframe.style.get('borderWidth'), 10) || 0) * 2)
        : 0;
    },
  );
};

export const makeGetInitialStyle = () => {
  const getComponentsKeyframes = makeGetComponentsKeyframes();
  const sortKeyframes = makeSortKeyframes();

  return createSelector(
    (state, props) => getComponentsKeyframes(state, props),
    keyframes => sortKeyframes(keyframes),
    (sortedKeyframes) => {
      if (sortedKeyframes.count() === 0) {
        return {};
      }

      const keyframe = sortedKeyframes.find(kf => kf.t === 0);
      return keyframe
        ? keyframe.style.toObject()
        : sortedKeyframes.first().style.toObject();
    },
  );
};

export const makeGetFirstVideoComponentsAssetsWithInitialStyle = () => {
  const sortKeyframes = makeSortKeyframes();
  return createSelector(
    state => getComponents(state).filter(c => c.type === 'video').first(),
    state => getAssets(state).filter(asset => asset.mime_type.includes('video')),
    state => getKeyframes(state),
    (firstVideoComponent, videoAssets, keyframes) => {
      const firstVideComponentKeyframes = firstVideoComponent.keyframes.map(keyframeID => keyframes.get(keyframeID));
      const initialStyle = sortKeyframes(firstVideComponentKeyframes).first().style;
      return videoAssets
        .filter(a => firstVideoComponent.getIn(['settings', 'assetIds']).includes(a.id))
        .map(cva => Object.assign({}, cva.toObject(), { initialStyle: initialStyle.toObject() }));
    },
  );
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/selectors/index.js